import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { LayoutDashboard, Users2, UserCog, ScrollText, PlayCircle, Mail, Copy, FileBadge, BarChart3, Trash2, KeyRound, BookOpen, Layers } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/sonner";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: "nav-admin-dashboard" },
  { to: "/admin/executives", label: "Marketing Executives", icon: UserCog, testId: "nav-admin-executives" },
  { to: "/admin/students", label: "Students", icon: Users2, testId: "nav-admin-students" },
  { to: "/admin/inbox", label: "Inbox", icon: Mail, testId: "nav-admin-inbox" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, testId: "nav-admin-audit" },
  { to: "/admin/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-admin-sample-report" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testId: "nav-admin-analytics" },
  { to: "/admin/how-it-works", label: "How It Works", icon: BookOpen, testId: "nav-admin-how-it-works" },
  { to: "/admin/questions", label: "Question Bank", icon: Layers, testId: "nav-admin-questions" },
  { to: "/me/demo", label: "Demo Mode", icon: PlayCircle, testId: "nav-admin-demo" },
];

export default function AdminExecutives() {
  const [execs, setExecs] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [topUpTarget, setTopUpTarget] = useState(null);
  const [topUpAmount, setTopUpAmount] = useState("3");

  const load = () => api.get("/admin/executives").then((res) => setExecs(res.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/executives", { name, email, company_name: companyName || null });
      toast.success("Marketing Executive created");
      setOpen(false); setName(""); setEmail(""); setCompanyName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create executive");
    }
  };

  const toggleStatus = async (e) => {
    const action = e.status === "ACTIVE" ? "disable" : "enable";
    await api.post(`/admin/executives/${e.me_id}/${action}`);
    load();
  };

  const resetAccess = async (meId) => {
    const res = await api.post(`/admin/executives/${meId}/reset`);
    toast.success(`New referral code: ${res.data.referral_code}`);
    load();
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/start?ref=${code}`);
    toast.success("Link copied");
  };

  const confirmDelete = async () => {
    try {
      const res = await api.post(`/admin/executives/${deleteTarget}/delete`);
      toast.success(`Executive deleted (${res.data.students_removed} student(s) removed)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete executive");
    }
    setDeleteTarget(null); load();
  };

  const submitTopUp = async () => {
    const amount = parseInt(topUpAmount, 10);
    if (!amount) return;
    try {
      const res = await api.post(`/admin/executives/${topUpTarget}/license-pool`, { amount });
      toast.success(`License pool updated — now ${res.data.license_pool}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update license pool");
    }
    setTopUpTarget(null); setTopUpAmount("3"); load();
  };

  return (
    <DashboardShell links={LINKS} activePath="/admin/executives">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="admin-executives-heading">Marketing Executives</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-executive-button" className="bg-indigo-600 hover:bg-indigo-700 rounded-full">Add Executive</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Marketing Executive</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input data-testid="new-executive-name-input" value={name} onChange={(e) => setName(e.target.value)} /></div>
              <div><Label>Email</Label><Input data-testid="new-executive-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
              <div><Label>Company Name (optional)</Label><Input data-testid="new-executive-company-input" placeholder="e.g. Bright Future Careers" value={companyName} onChange={(e) => setCompanyName(e.target.value)} /></div>
            </div>
            <DialogFooter>
              <Button data-testid="create-executive-submit-button" onClick={create} className="bg-indigo-600 hover:bg-indigo-700">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="admin-executives-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>ID</TableHead><TableHead>Code</TableHead><TableHead>Status</TableHead>
              <TableHead>License Pool</TableHead>
              <TableHead>Clicks</TableHead><TableHead>Registrations</TableHead><TableHead>Activations</TableHead>
              <TableHead>Started</TableHead><TableHead>Completed</TableHead><TableHead>Reports</TableHead><TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {execs.map((e) => (
              <TableRow key={e.me_id} data-testid={`executive-row-${e.me_id}`}>
                <TableCell>{e.name}</TableCell>
                <TableCell className="font-mono text-xs">{e.me_id}</TableCell>
                <TableCell className="font-mono text-xs">{e.referral_code}</TableCell>
                <TableCell><Badge variant={e.status === "ACTIVE" ? "default" : "secondary"}>{e.status}</Badge></TableCell>
                <TableCell className="font-mono font-semibold" data-testid={`license-pool-${e.me_id}`}>{e.license_pool ?? 0}</TableCell>
                <TableCell>{e.clicks}</TableCell>
                <TableCell>{e.registrations}</TableCell>
                <TableCell>{e.activations}</TableCell>
                <TableCell>{e.started}</TableCell>
                <TableCell>{e.completed}</TableCell>
                <TableCell>{e.reports_ready}</TableCell>
                <TableCell className="space-x-1 whitespace-nowrap">
                  <Button size="icon" variant="ghost" data-testid={`copy-link-button-${e.me_id}`} onClick={() => copyLink(e.referral_code)}><Copy className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" data-testid={`toggle-status-button-${e.me_id}`} onClick={() => toggleStatus(e)}>{e.status === "ACTIVE" ? "Disable" : "Enable"}</Button>
                  <Button size="sm" variant="outline" data-testid={`reset-access-button-${e.me_id}`} onClick={() => resetAccess(e.me_id)}>Reset</Button>
                  <Button size="sm" variant="outline" data-testid={`top-up-license-button-${e.me_id}`} onClick={() => setTopUpTarget(e.me_id)}><KeyRound className="h-3.5 w-3.5 mr-1" />Add Licenses</Button>
                  <Button size="icon" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`delete-executive-button-${e.me_id}`} onClick={() => setDeleteTarget(e.me_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-executive-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Marketing Executive permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This also permanently deletes all students referred by this executive, along with their sessions, licenses and reports. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-executive-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-executive-confirm-button" className="bg-red-600 hover:bg-red-700" onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!topUpTarget} onOpenChange={(v) => !v && setTopUpTarget(null)}>
        <DialogContent data-testid="top-up-license-dialog">
          <DialogHeader><DialogTitle>Add Licenses to Pool</DialogTitle></DialogHeader>
          <Label>Number of licenses to add</Label>
          <Input type="number" min="1" value={topUpAmount} data-testid="top-up-amount-input" onChange={(e) => setTopUpAmount(e.target.value)} />
          <DialogFooter><Button data-testid="top-up-submit-button" onClick={submitTopUp}>Add to Pool</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
